// src/pages/DeviceDetailPage.js
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { devices as devicesApi, students as studentsApi, settings as settingsApi } from '../utils/api';

const DEV_STATUS = { enabled: 'badge-success', disabled: 'badge-danger', removed: 'badge-muted' };

export default function DeviceDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['device', id],
    queryFn: () => devicesApi.get(id).then(r => r.data.data)
  });

  const statusMut = useMutation({
    mutationFn: ({ status, reason }) => devicesApi.setStatus(id, status, reason),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries(['device', id]); },
    onError: err => toast.error(err.response?.data?.message || 'Failed')
  });

  const deleteMut = useMutation({
    mutationFn: () => devicesApi.delete(id),
    onSuccess: () => {
      toast.success('Device deleted');
      if (data?.student_id) qc.invalidateQueries(['student', String(data.student_id)]);
      navigate(data?.student_id ? `/students/${data.student_id}` : '/devices');
    },
    onError: err => toast.error(err.response?.data?.message || 'Failed to delete device')
  });

  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => settingsApi.appSettings().then(r => r.data.data)
  });
  const { data: student } = useQuery({
    queryKey: ['student', data?.student_id ? String(data.student_id) : null],
    queryFn: () => studentsApi.get(data.student_id).then(r => r.data.data),
    enabled: !!data?.student_id
  });

  if (isLoading) return <div className="loading-page"><span className="spinner" style={{ width: 36, height: 36 }} /></div>;
  if (!data) return <div className="empty-state">Device not found</div>;

  const d = data;
  const maxDevices = parseInt(appSettings?.max_devices_per_student ?? 2, 10);
  const otherEnabledCount = (student?.devices || []).filter(x => x.status === 'enabled' && x.id !== d.id).length;
  const limitReached = d.status !== 'enabled' && otherEnabledCount >= maxDevices;

  return (
    <div>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: 8 }}>
        <Link to="/devices">Devices</Link> → {d.mac_address}
      </div>

      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ fontFamily: 'var(--font-mono)' }}>{d.mac_address}</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>{d.vendor_name || 'Unknown vendor'} · {d.device_type}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span className={`badge ${DEV_STATUS[d.status]}`} style={{ fontSize: '0.85rem' }}>{d.status}</span>
          {d.status === 'enabled' && (
            <button className="btn btn-danger btn-sm"
              onClick={() => {
                if (!window.confirm(`Disable device ${d.mac_address}? This will cut off its network access.`)) return;
                const r = prompt('Reason for disabling (optional):');
                if (r === null) return;
                statusMut.mutate({ status: 'disabled', reason: r });
              }}>
              Disable
            </button>
          )}
          {d.status === 'disabled' && (
            <button className="btn btn-success btn-sm"
              disabled={limitReached}
              title={limitReached ? `Limit of ${maxDevices} enabled device(s) reached — disable another device first` : undefined}
              onClick={() => { if (window.confirm(`Enable device ${d.mac_address}? This will grant it network access.`)) statusMut.mutate({ status: 'enabled' }); }}>
              Enable
            </button>
          )}
          {d.status !== 'removed' && (
            <button className="btn btn-danger btn-sm"
              onClick={() => { if (window.confirm('Mark device as removed?')) statusMut.mutate({ status: 'removed' }); }}>
              Remove
            </button>
          )}
          {d.status === 'disabled' && (
            <button className="btn btn-danger btn-sm"
              onClick={() => { if (window.confirm('Permanently delete this device? This cannot be undone.')) deleteMut.mutate(); }}>
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="detail-grid">
        {/* Device info */}
        <div className="card">
          <div className="card-header"><span className="card-title">Device Information</span></div>
          {[
            ['MAC Address',   d.mac_address],
            ['MAC (Hex)',     d.mac_normalized],
            ['OUI Prefix',   d.oui_prefix],
            ['Vendor',       d.vendor_name || '—'],
            ['Model',        d.device_model || '—'],
            ['Type',         d.device_type],
            ['Status',       d.status],
            ['Registered',   new Date(d.created_at).toLocaleString()],
            ['Updated',      new Date(d.updated_at).toLocaleString()],
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>{label}</span>
              <span className={['MAC Address','MAC (Hex)','OUI Prefix'].includes(label) ? 'text-mono' : ''}>{val}</span>
            </div>
          ))}
        </div>

        {/* Owner info */}
        <div className="card">
          <div className="card-header"><span className="card-title">Owner</span></div>
          <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>{d.student_name}</div>
            <div style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)', fontSize: '0.82rem', marginTop: 4 }}>{d.registration_no}</div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Link to={`/students/${d.student_id}`} className="btn btn-secondary btn-sm">View Student Profile →</Link>
          </div>

          {d.notes && (
            <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-hover)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--text-dim)' }}>
              📝 {d.notes}
            </div>
          )}
        </div>
      </div>

      {/* Status history */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header"><span className="card-title">Status History</span></div>
        {!d.statusHistory || d.statusHistory.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px' }}>No status changes recorded</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>From</th><th>To</th><th>Changed By</th><th>Reason</th><th>Date</th></tr>
              </thead>
              <tbody>
                {d.statusHistory.map(h => (
                  <tr key={h.id}>
                    <td><span className={`badge ${DEV_STATUS[h.old_status]}`}>{h.old_status}</span></td>
                    <td><span className={`badge ${DEV_STATUS[h.new_status]}`}>{h.new_status}</span></td>
                    <td>{h.changed_by_name || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontStyle: h.reason ? 'normal' : 'italic' }}>{h.reason || 'No reason given'}</td>
                    <td className="text-mono">{new Date(h.changed_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
